import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {Box, Stack, Typography} from "@mui/material";
import {blue, green, grey} from "@mui/material/colors";

import _ from "lodash";
import {getCustomerBillingInvoice} from "@/services/customer.ts";

import dayjs from "dayjs";
import React, {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {DataGridSimpleStyled, GridColDef} from "@/components/Shared/DataGridSimple";

import Utils from "@/components/Shared/Utils";
import {LoadingBackdrop} from '@/components/Layout/DefaultLayout';
import UICard, {UICardContent, UICardHeadingLight} from "@/components/Shared/Utils/UICard";

export default function AccountBillingOverview({customer, user}: any) {
    const navigate = useNavigate();
    const [filter, setFilter] = useState<number | null>(1);
    const {data: invoices} = getCustomerBillingInvoice(customer);

    const columns: GridColDef[] = [
        {
            field: 'id',
            headerName: 'ID',
            width: 100,
            editable: false,
            filterable: false,
            valueGetter: ({row}) => row.invoice_number || row.invoice_id,
            renderCell: ({row, value}) => <Typography fontSize={13}
                                                      onClick={() => navigate(`/account/billing/invoices/${row.invoice_id}`)}
                                                      sx={{cursor: 'pointer', color: blue[900]}}>{value}</Typography>
        },
        {
            field: 'invoice_description',
            headerName: 'Description',
            width: 450,
            editable: false,
            filterable: false,
            renderCell: ({value}) => <Typography fontSize={13}>{value}</Typography>
        },
        {
            field: 'invoice_total',
            headerName: 'Amount',
            width: 150,
            editable: false,
            filterable: false,
            valueGetter: ({row}) => `đ${row.invoice_total?.toLocaleString()}`,
            renderCell: ({value}) => <Typography fontSize={13}>{value}</Typography>
        },
        {
            field: 'invoice_status',
            headerName: 'Status',
            width: 100,
            editable: false,
            filterable: false,
            renderCell: ({row}) => row.invoice_status === 2 ? (
                <Typography fontSize={13} sx={{color: green[500]}}>Paid</Typography>
            ) : (
                <Typography fontSize={13}>Unpaid</Typography>
            )
        },
        {
            field: 'invoice_created_at',
            headerName: 'Created At',
            width: 150,
            editable: false,
            filterable: false,
            valueGetter: ({row}) => dayjs(row.invoice_created_at).format("MMM DD, YYYY"),
            renderCell: ({value}) => <Typography fontSize={13}>{value}</Typography>
        },
        {
            field: 'invoice_duedate',
            headerName: 'Invoice Due',
            width: 150,
            editable: false,
            filterable: false,
            valueGetter: ({row}) => row.invoice_status === 1 ? dayjs(row.invoice_duedate).format("MMM DD, YYYY") : '-',
            renderCell: ({value}) => <Typography fontSize={13}>{value}</Typography>
        }
    ]

    const unpaid = _.sumBy(invoices?.filter((item: any) => item.invoice_status === 1), (item: any) => item.invoice_total)

    const items = useMemo(() => filter ? invoices?.filter((item: any) => item.invoice_status === filter) : invoices, [filter, invoices])

    return (
        <Utils.LayoutUtils.MainContainer>
            <React.Suspense fallback={<LoadingBackdrop/>}>
                <Utils.LayoutUtils.MainWrapper>
                <Utils.LayoutUtils.MainHeading
                    title="Customer Information"
                    description={`${customer?.customer_id || ''} - ${customer?.customer_email || user?.email}`}
                />
                    <UICard gradient>
                        <UICardHeadingLight title="Credit Balance"/>
                        <UICardContent>
                            <Box p={2}>
                                <Typography fontSize={20} fontWeight={600}>
                                    đ{(customer?.customer_credit || 0)?.toLocaleString()}
                                </Typography>
                            </Box>
                        </UICardContent>
                    </UICard>
                    <UICard gradient>
                        <UICardHeadingLight title={`Invoices (${invoices?.length || 0})`}
                                            description="View and manage your invoices."/>
                        <UICardContent>
                            <Box p={2}>
                                <Box mb={2}>
                                    <Utils.LayoutUtils.MainInformationFact>
                                        <Typography fontSize={13} fontWeight={700}>
                                            Pending Settlement
                                        </Typography>
                                        <Typography mb={1} fontSize={13} color={grey[700]}>
                                            | Unpaid
                                        </Typography>
                                        <Typography fontSize={20} fontWeight={700} color={blue[900]}>
                                            đ{(unpaid || 0)?.toLocaleString()}
                                        </Typography>
                                    </Utils.LayoutUtils.MainInformationFact>
                                </Box>
                                <Stack mb={2} direction="row" justifyContent="end">
                                    <Utils.FormUtils.PrimaryButton
                                        light
                                        startIcon={filter ? <VisibilityOffOutlinedIcon/> : <VisibilityOutlinedIcon/>}
                                        onClick={() => setFilter(filter ? null : 1)}
                                    >
                                        Show Paid Invoice
                                    </Utils.FormUtils.PrimaryButton>
                                </Stack>
                                <DataGridSimpleStyled rows={items || []} columns={columns}/>
                            </Box>
                        </UICardContent>
                    </UICard>
                </Utils.LayoutUtils.MainWrapper>
            </React.Suspense>
        </Utils.LayoutUtils.MainContainer>
    )
}